import {
  takeLatest,
  all,
  select,
  put,
  call,
  delay,
  type SagaReturnType,
} from 'redux-saga/effects';
import { push } from 'connected-react-router';

import { logger } from 'src/error';
import { api } from 'src/api';
import { isElementVisible } from 'src/utils/globalUtils';
import { scrollToElement } from 'src/utils/domUtils';
import { takeOnce } from 'src/helpers/effects';

import { getFullPath } from 'checkout/utils/globalUtils';

import { initAppAction } from 'src/store/view/actions';
import { commitSearchParamsAction } from 'src/routes/Hotel/store/actions/searchParams';
import { setSupplierFieldsData } from 'checkout/CheckoutRoutes/HotelCheckout/store/domains/supplierFields/slice';

import { getIsMobile } from 'src/store/view/selectors';
import { getClientUnconfirmedPhone } from 'src/store/client/selectors';

import {
  TOURISTS_COMPONENT_ID,
  LOG_IN_ID,
  CLIENT_DATA_ID,
  PAYMENT_COMPONENT,
} from '../constants/tourists';
import {
  ROOT_COMPONENT_ID,
  ROUTE_TRANSITION_TIME,
  PAGES,
  PACKAGE_HOME_PATH,
  TOURISTS_PATH,
  PAY_PATH,
  SUCCESS_PATH,
} from '../constants';
import { FLIGHTS_COMPONENT_ID } from '../../CheckoutRoutes/PackageCheckout/store/flights/constants';

import { getIsRouteTransition } from '../selectors/view';
import { getPath, getIsPayPath } from '../selectors/router';
import {
  getOrderIsCreated,
  getOrderClientConfirmed,
  getOrderTouristsConfirmed,
} from '../selectors/order';

import { checkPriceReductionByCashbackAction } from '../actions/order';

import {
  scrollToPaymentAction,
  scrollToTouristsAction,
  scrollToTopAction,
  goToPathAction,
  setScrollPositionAction,
  expandTouristFormAction,
  scrollToLogInAction,
  goToHotelSectionAction,
  goToPayAction,
  goToClientAction,
  goToFlightSectionAction,
  scrollToBooking,
  goToClientDataAction,
  goToTouristsAction,
  goToTouristsHotelCheckoutAction,
} from '../actions/view';

import {
  getIsManualPackage,
  getArchivedAt,
  getIsFuture,
  getPackageId,
  getTouristsCount,
} from '../selectors/package';
import { getHotelAnalytics, getHotelRoulette } from '../selectors/hotel';
import { startActualizationProcessAction } from '../actions/actualization';
import { loadTestimonials } from '../../CheckoutRoutes/PackageCheckout/store/testimonials/actions';
import { similarPackagesStartAction } from '../actions/similar-packages';
import { getIsTouristValidatedCount } from '../selectors/data';
import { getPackagesForRoomUpgradeSuccess } from '../reducers/extra-rooms';
import { updatePackageId } from '../actions/package';

/**
 * Ждет элемент и вызывает скролл к нему
 * @param {string} elementId
 */
function* handleScrollToElement(elementId: string) {
  while (true) {
    const element = document.getElementById(elementId);
    const isRouteTransition: SagaReturnType<typeof getIsRouteTransition> =
      yield select(getIsRouteTransition);

    if (element && !isRouteTransition) {
      scrollToElement(element);
      break;
    }

    yield delay(ROUTE_TRANSITION_TIME);
  }
}

function* checkBeforeTransition(path) {
  const page = PAGES.find((item) => item.path === path);

  if (!page || !page.transitionCondition) {
    return true;
  }

  return yield select(page.transitionCondition as (state) => boolean);
}

function* goTo(path) {
  try {
    const isTransitionAllowed = yield checkBeforeTransition(path);
    const currentPath: SagaReturnType<typeof getPath> = yield select(getPath);
    const nextPath = getFullPath(path);

    if (currentPath === nextPath) {
      return;
    }

    if (isTransitionAllowed) {
      yield put(push(nextPath));
    }
  } catch (error) {
    logger(error, 'Package goTo');
  }
}

function* goToPath({ payload }: ReturnType<typeof goToPathAction>) {
  const isCreated: SagaReturnType<typeof getOrderIsCreated> = yield select(
    getOrderIsCreated,
  );
  const isTransitionAllowed = !isCreated || payload === SUCCESS_PATH;

  if (isTransitionAllowed) {
    yield goTo(payload);
  }
}

function* handleScrollToTop() {
  yield handleScrollToElement(ROOT_COMPONENT_ID);
}

function* handleScrollToTourists() {
  yield handleScrollToElement(TOURISTS_COMPONENT_ID);
}

function* handleScrollToLogIn() {
  yield handleScrollToElement(LOG_IN_ID);
}

function* handleGoToHotel() {
  yield put(goToPathAction(PACKAGE_HOME_PATH));
  yield handleScrollToTop();
}

function* handleGoToFlight() {
  yield put(goToPathAction(PACKAGE_HOME_PATH));
  yield handleScrollToElement(FLIGHTS_COMPONENT_ID);
}

function* handleGoToClient() {
  yield put(goToPathAction(TOURISTS_PATH));
  yield put(scrollToTopAction());
  yield put(checkPriceReductionByCashbackAction());
}

function* handleGoToClientData() {
  yield put(goToPathAction(PACKAGE_HOME_PATH));
  yield handleScrollToElement(CLIENT_DATA_ID);
}

function* handleGoToPayment() {
  yield handleScrollToElement(PAYMENT_COMPONENT);
}

function* handleGoToPay() {
  yield put(goToPathAction(PAY_PATH));
}

/**
 * Проверяет, можем ли мы перейти на туристов,
 * и если не можем, то перекидывает к клиенту
 */
function* handleGoToTourists({
  payload,
}: ReturnType<typeof goToTouristsAction>) {
  yield put(goToPathAction(TOURISTS_PATH));
  const isClientConfirmed: SagaReturnType<typeof getOrderClientConfirmed> =
    yield select(getOrderClientConfirmed);
  if (isClientConfirmed) {
    yield put(scrollToTouristsAction());

    const isMobile: SagaReturnType<typeof getIsMobile> = yield select(
      getIsMobile,
    );

    if (isMobile) {
      yield put(expandTouristFormAction(payload));
    }
  }
}

/**
 * Проверяет, можем ли мы перейти на туристов,
 * и если не можем, то перекидывает к клиенту
 * на отельном чекауте
 */
function* handleGoToTouristsHotelCheckout({
  payload,
}: ReturnType<typeof goToTouristsHotelCheckoutAction>) {
  yield put(goToPathAction(PACKAGE_HOME_PATH));
  const isTouristsConfirmed: SagaReturnType<typeof getOrderTouristsConfirmed> =
    yield select(getOrderTouristsConfirmed);
  if (isTouristsConfirmed) {
    yield handleScrollToElement(`${TOURISTS_COMPONENT_ID}_${payload}`);
  }
}

/**
 * Если пользователь не авторизовался, то скролит пользователя к
 * форме авторизации, если пользователь вошел в систему, то скролит к вводу
 * данных туристов
 */
function* handleGoToBooking() {
  const unconfirmedPhone: SagaReturnType<typeof getClientUnconfirmedPhone> =
    yield select(getClientUnconfirmedPhone);
  if (unconfirmedPhone === null) {
    yield put(scrollToTouristsAction());
  } else {
    yield put(scrollToLogInAction());
  }
  const validatedTouristsCount: SagaReturnType<
    typeof getIsTouristValidatedCount
  > = yield select(getIsTouristValidatedCount);
  const touristsCount: SagaReturnType<typeof getTouristsCount> = yield select(
    getTouristsCount,
  );
  if (validatedTouristsCount === touristsCount) {
    yield put(scrollToPaymentAction());
  }
}

function* handleScrollToPosition() {
  const position = window.pageYOffset || document.documentElement.scrollTop;

  yield put(setScrollPositionAction(position));
}

// сука тут
function* handleScrollToExtraRooms() {
  const isServicesPath: SagaReturnType<typeof getIsPayPath> = yield select(
    getIsPayPath,
  );

  const container = document.querySelector('.checkout-extra-rooms');
  const isVisible = container && isElementVisible(container);
  const check = !isVisible && isServicesPath;

  if (check) {
    yield call(handleScrollToPosition);
  }
}

function* handleInitApp() {
  const isManualPackage: SagaReturnType<typeof getIsManualPackage> =
    yield select(getIsManualPackage);
  const isRoulette: SagaReturnType<typeof getHotelRoulette> = yield select(
    getHotelRoulette,
  );
  const isArchived: SagaReturnType<typeof getArchivedAt> = yield select(
    getArchivedAt,
  );
  const isFuture: SagaReturnType<typeof getIsFuture> = yield select(
    getIsFuture,
  );

  if (!isManualPackage && !isArchived) {
    yield trasformSearchParams();
    const packageId: SagaReturnType<typeof getPackageId> = yield select(
      getPackageId,
    );
    yield put(startActualizationProcessAction());
    try {
      const { supplier_fields } = yield api.getHotelsSuplierField({
        package_id: packageId,
      });
      yield put(setSupplierFieldsData(supplier_fields));
    } catch (error) {
      logger(error, 'supplier fields is not available');
    }
  }

  if (isArchived && isFuture) {
    yield put(similarPackagesStartAction());
  }

  if (!isRoulette) {
    yield put(loadTestimonials());
  }
}

function* trasformSearchParams() {
  const hotelInfo = yield select(getHotelAnalytics);
  const hotel = {
    id: hotelInfo.id,
    name: hotelInfo.name,
    name_ru: hotelInfo?.name_ru ?? hotelInfo.name,
    public_url: hotelInfo.public_url,
  };
  const destinationInfo = { ...window.SEARCH_PARAMS.to, hotel };
  yield put(
    commitSearchParamsAction({ ...window.SEARCH_PARAMS, to: destinationInfo }),
  );
}

export default function* viewCheckoutWathcer() {
  yield all([
    takeLatest(initAppAction, handleInitApp),
    takeLatest(goToPathAction, goToPath),
    takeLatest(scrollToTouristsAction, handleScrollToTourists),
    takeLatest(scrollToTopAction, handleScrollToTop),
    takeLatest(goToHotelSectionAction, handleGoToHotel),
    takeLatest(goToFlightSectionAction, handleGoToFlight),
    takeLatest(goToClientAction, handleGoToClient),
    takeLatest(goToClientDataAction, handleGoToClientData),
    takeLatest(goToTouristsAction, handleGoToTourists),
    takeLatest(
      goToTouristsHotelCheckoutAction,
      handleGoToTouristsHotelCheckout,
    ),
    takeLatest(goToPayAction, handleGoToPay),
    takeLatest(updatePackageId, handleScrollToPosition),
    takeLatest(scrollToPaymentAction, handleGoToPayment),
    takeOnce(getPackagesForRoomUpgradeSuccess, handleScrollToExtraRooms),
    takeLatest(scrollToBooking, handleGoToBooking),
    takeLatest(scrollToLogInAction, handleScrollToLogIn),
  ]);
}
